import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { OptImg } from '../ui/OptImg';

const srcOf = (p) => (p.image?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${p.image}` : p.image);

// Full-screen project viewer: arrows, keyboard (← → Esc) and swipe.
export const GalleryLightbox = ({ projects, index, onClose, onIndex }) => {
  const touch = useRef(null);
  const closeRef = useRef(null);
  const self = (fn) => (e) => { if (e.target === e.currentTarget) fn(); };
  const p = projects[index];
  const go = useCallback((d) => onIndex((index + d + projects.length) % projects.length), [index, projects.length, onIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [go, onClose]);

  if (!p) return null;
  const nav = 'absolute top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors';
  return createPortal(
    <div className="fixed inset-0 z-[1000] bg-black/95 flex flex-col" role="dialog" aria-modal="true" aria-label={p.title} onClick={self(onClose)} data-testid="gallery-lightbox"
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - (touch.current ?? e.changedTouches[0].clientX); if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1); }}>
      <div className="flex items-center justify-between px-4 md:px-8 py-4 text-white/70 text-sm">
        <span data-testid="gallery-lightbox-counter">{index + 1} / {projects.length}</span>
        <button ref={closeRef} type="button" onClick={onClose} className="w-10 h-10 rounded-full hover:bg-white/10 flex items-center justify-center" aria-label="Close" data-testid="gallery-lightbox-close"><X className="w-5 h-5" /></button>
      </div>
      <div className="relative flex-1 flex items-center justify-center px-4 md:px-20 min-h-0" onClick={self(onClose)}>
        <OptImg key={p.id || index} src={srcOf(p)} alt={p.title} sizes="90vw" className="max-h-full max-w-full object-contain rounded" onClick={(e) => e.stopPropagation()} data-testid="gallery-lightbox-image" />
        {projects.length > 1 && <>
          <button type="button" onClick={(e) => { e.stopPropagation(); go(-1); }} className={`${nav} left-2 md:left-6`} aria-label="Previous" data-testid="gallery-lightbox-prev"><ChevronLeft className="w-6 h-6" /></button>
          <button type="button" onClick={(e) => { e.stopPropagation(); go(1); }} className={`${nav} right-2 md:right-6`} aria-label="Next" data-testid="gallery-lightbox-next"><ChevronRight className="w-6 h-6" /></button>
        </>}
      </div>
      <div className="px-4 md:px-8 py-5 text-center" onClick={self(onClose)}>
        <p className="text-white font-serif text-xl" data-testid="gallery-lightbox-title">{p.title}</p>
        {p.description && <p className="text-white/60 text-sm mt-1 max-w-2xl mx-auto">{p.description}</p>}
      </div>
    </div>,
    document.body,
  );
};
